import { Address } from '@eshop/types/location.types.ts';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShippingMethod } from '@eshop/types/shipping.types.ts';
import { Alert, Button, Card, Col, Empty, Radio, Row, Space, Tag, Typography } from 'antd';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { ModernAddressDetails } from '@eshop/components/location/AddressCards.tsx';
import { CartItem } from '@eshop/types/cart.types.ts';
import { logInfo } from '@tools/LogsHandler.tsx';
import { InfoCircleOutlined } from '@ant-design/icons';
import { connsPublic } from '@cfg/axiosConfig.tsx';
import { mapApiToShipping } from './shippingMapper.ts';
import { useAutoSelectSingleOption } from '@eshop/hooks/useCheckoutFlow.ts';
import { MethodSkeleton } from './MethodSkeleton';
import { ShippingMethodCard } from './ShippingMethodCard';
import './ShippingMethodCard.css';

const { Text } = Typography;

const DEFAULT_SHIPPING_CODES = {
    TAKEAWAY: 'takeaway',
    TO_AGREE: 'to_agree',
} as const;

const getMethodCode = (method: ShippingMethod): string => method.codigo ?? method.code ?? '';

const isTakeawayMethod = (method: ShippingMethod): boolean => {
    const code = getMethodCode(method);
    return code === DEFAULT_SHIPPING_CODES.TAKEAWAY || method.strategy === 'pickup';
};

const isToAgreeMethod = (method: ShippingMethod): boolean => {
    const code = getMethodCode(method);
    return code === DEFAULT_SHIPPING_CODES.TO_AGREE || method.strategy === 'manual';
};

const getMethodCost = (method: ShippingMethod): number => {
    const raw = method.calculated_cost?.incl_tax;
    return typeof raw === 'number' ? raw : 0;
};

const getMethodCurrency = (method: ShippingMethod): string => method.calculated_cost?.currency ?? 'ARS';

type LocationHints = {
    city?: string;
    postal_code?: string;
    state?: string;
};

type ShippingErrorState = {
    status?: number;
    message: string;
};

const sanitizeQueryValue = (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : undefined;
};

const getLocationHints = (address: Address): LocationHints => {
    const raw = address as Record<string, unknown>;
    const rawCity = address.verbose?.city ?? raw.city;
    const rawPostal =
        (raw.postal_code as string | number | undefined | null) ??
        (raw.postcode as string | number | undefined | null) ??
        address.postcode ??
        address.verbose?.postcode ??
        (raw.postcode_code as string | number | undefined | null);
    const rawState = raw.state_code ?? raw.state;

    return {
        city: sanitizeQueryValue(rawCity),
        postal_code: sanitizeQueryValue(rawPostal),
        state: sanitizeQueryValue(rawState),
    };
};

const buildQueryParams = (hints: LocationHints): Record<string, string> => {
    const params: Record<string, string> = {};
    if (hints.city) params.city = hints.city;
    if (hints.postal_code) params.postal_code = hints.postal_code;
    if (hints.state) params.state = hints.state;
    return params;
};

interface ShippingSectionProps {
    selectedAddress: Address | undefined;
    cartItems?: CartItem[];
    onShippingMethodChange: (data: { id: number | undefined; cost: number }) => void;
    className?: string;
    totalCart?: number;
    onAutoSelectNext?: () => void;
    onSelectedShippingMethodChange?: (method: ShippingMethod | undefined) => void;
    id?: number;
}

export const ShippingSection: React.FC<ShippingSectionProps> = ({
    selectedAddress,
    onShippingMethodChange,
    className,
    totalCart,
    onAutoSelectNext,
    onSelectedShippingMethodChange,
    id,
}) => {
    const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
    const [shippingLoading, setShippingLoading] = useState<boolean>(false);
    const [selectedShippingMethod, setSelectedShippingMethod] =
        useState<ShippingMethod | undefined>();
    const [previousSelection, setPreviousSelection] = useState<ShippingMethod | null>(null);
    const [envioGratisDesde, setEnvioGratisDesde] = useState<number | undefined>();
    const [errorState, setErrorState] = useState<ShippingErrorState | null>(null);

    const lastLocation = React.useRef<LocationHints | null>(null);

    useEffect(() => {
        if (!selectedAddress) {
            lastLocation.current = null;
            onSelectedShippingMethodChange?.(undefined);
            return;
        }

        const locationHints = getLocationHints(selectedAddress);
        if (
            lastLocation.current &&
            lastLocation.current.city === locationHints.city &&
            lastLocation.current.postal_code === locationHints.postal_code &&
            lastLocation.current.state === locationHints.state
        ) {
            return; // misma ciudad y CP, evitar refetch
        }
        lastLocation.current = locationHints;
        setErrorState(null);
        void fetchShippingMethods(selectedAddress, locationHints);
    }, [selectedAddress]);  // eslint-disable-line

    // Sincronizar selección inicial basada en props (restauración de estado)
    useEffect(() => {
        if (typeof id !== 'number' || shippingMethods.length === 0) {
            return;
        }
        const match = shippingMethods.find((method) => method.id === id);
        if (!match) {
            return;
        }
        if (selectedShippingMethod?.id === match.id) {
            return;
        }
        setSelectedShippingMethod(match);
        onShippingMethodChange({
            id: match.id,
            cost: getMethodCost(match),
        });
        onSelectedShippingMethodChange?.(match);
    }, [id, shippingMethods, selectedShippingMethod, onShippingMethodChange, onSelectedShippingMethodChange]);

    const fetchShippingMethods = async (address: Address, locationHints?: LocationHints) => {
        try {
            setShippingLoading(true);
            setErrorState(null);
            setEnvioGratisDesde(undefined);
            const hints = locationHints ?? getLocationHints(address);
            const response = await connsPublic<unknown[]>(
                'eshop:shipping:active_list'
            ).get(buildQueryParams(hints));
            logInfo('ShippingSection - fetchShippingMethods - response.data:', response.data);
            const mapped = response.data.map(mapApiToShipping);
            const deliveryMethods = mapped.filter(
                (method) => !isTakeawayMethod(method)
                    && (!isToAgreeMethod(method) || method.is_fallback)
            );
            const pricedMethods = deliveryMethods.filter(
                (method) => method.calculated_cost.incl_tax != null
            );
            const sortedPriced = pricedMethods.sort(
                (a, b) => getMethodCost(a) - getMethodCost(b)
            );
            const fallbackMethods = deliveryMethods.filter((method) => method.is_fallback);
            const sortedMethods = [
                ...sortedPriced.filter((method) => !method.is_fallback),
                ...fallbackMethods,
            ];

            setEnvioGratisDesde(
                sortedPriced
                    .filter((method) => method.limite_envio_gratis > 0)
                    .sort((a, b) => a.limite_envio_gratis - b.limite_envio_gratis)[0]?.limite_envio_gratis
            );

            logInfo('fetchShippingMethods - sortedMethods: ', sortedMethods);
            setShippingMethods(sortedMethods);
            const currentSelected = selectedShippingMethod;
            const match = currentSelected
                ? sortedMethods.find((method) => method.id === currentSelected.id)
                : undefined;

            if (match) {
                setSelectedShippingMethod(match);
                onShippingMethodChange({
                    id: match.id,
                    cost: getMethodCost(match),
                });
                onSelectedShippingMethodChange?.(match);
            } else {
                setSelectedShippingMethod(undefined);
                onShippingMethodChange({ id: undefined, cost: 0 });
                onSelectedShippingMethodChange?.(undefined);
            }
        } catch (error) {
            logInfo('Failed to fetch shipping methods:', error);
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            const isAuthError = status === 401 || status === 403;
            const message = isAuthError
                ? 'No pudimos cargar los métodos de envío sin una sesión activa. Podés iniciar sesión o intentar nuevamente.'
                : 'No pudimos cargar los métodos de envío. Revisá tu conexión e intentá nuevamente.';
            setErrorState({ status, message });
            setShippingMethods([]);
            setSelectedShippingMethod(undefined);
            setEnvioGratisDesde(undefined);
            onShippingMethodChange({ id: undefined, cost: 0 });
            onSelectedShippingMethodChange?.(undefined);
            lastLocation.current = null;
        } finally {
            setShippingLoading(false);
        }
    };

    const { markManualSelection } = useAutoSelectSingleOption<ShippingMethod, number>({
        options: shippingMethods,
        selectedValue: selectedShippingMethod?.id ?? id,
        isLoading: shippingLoading,
        getOptionValue: (method) => method.id,
        onSelect: (id, method) => {
            setSelectedShippingMethod(method);
            onShippingMethodChange({
                id,
                cost: getMethodCost(method),
            });
            onSelectedShippingMethodChange?.(method);
        },
        onAutoSelect: () => {
            onAutoSelectNext?.();
        },
        disabled: !selectedAddress,
    });

    const handleShippingMethodSelect = (methodId: number) => {
        markManualSelection();
        const selectedMethod = shippingMethods.find((method) => method.id === methodId);
        if (selectedMethod) {
            onShippingMethodChange({
                id: methodId,
                cost: getMethodCost(selectedMethod),
            });
        } else {
            onShippingMethodChange({ id: undefined, cost: 0 });
        }
        setSelectedShippingMethod(selectedMethod);
        onSelectedShippingMethodChange?.(selectedMethod);
    };

    const handleRetry = () => {
        if (!selectedAddress) {
            return;
        }
        // Preserve current selection before retry
        if (selectedShippingMethod) {
            setPreviousSelection(selectedShippingMethod);
        }
        const hints = getLocationHints(selectedAddress);
        lastLocation.current = hints;
        void fetchShippingMethods(selectedAddress, hints);
    };

    return (
        <Card title="Método de Envío" className={className} variant="outlined">
            {selectedAddress ? (
                shippingLoading ? (
                    <MethodSkeleton count={3} />
                ) : errorState ? (
                    <Alert
                        type="error"
                        showIcon
                        title="No pudimos cargar los métodos de envío"
                        description={
                            <Space orientation="vertical" size="small">
                                <Text type="secondary">
                                    {errorState.message}
                                </Text>
                                {previousSelection && (
                                    <Text type="secondary">
                                        Tu selección anterior ({previousSelection.name}) se restaurará automáticamente.
                                    </Text>
                                )}
                            </Space>
                        }
                        action={(
                            <Button
                                type="primary"
                                size="small"
                                onClick={handleRetry}
                                disabled={shippingLoading}
                            >
                                Reintentar
                            </Button>
                        )}
                    />
                ) : shippingMethods.length > 0 ? (
                    <Row>
                        {shippingMethods.some((method) => method.is_fallback) ? (
                            <Col xs={24}>
                                <Alert
                                    type="info"
                                    showIcon
                                    title="Entrega a coordinar"
                                    description="No encontramos una alternativa disponible para esta dirección. Podrás coordinar la entrega con la tienda."
                                    className="mb-3"
                                />
                            </Col>
                        ) : null}
                        {envioGratisDesde && totalCart && envioGratisDesde > totalCart && (
                            <Col xs={24}>
                                <p className="fs-italic font-italic" style={{ color: '#EB2F96' }}>
                                    Te faltan solo <b>{getFormattedPrice(envioGratisDesde - totalCart, 'ARS')}</b> para
                                    conseguir el envío gratis!
                                </p>
                            </Col>
                        )}
                        <Col xs={24}>
                            <div className="shipping-methods-grid">
                                {shippingMethods.map((method) => {
                                    const hasExplicitCost = typeof method.calculated_cost?.incl_tax === 'number';
                                    const costLabel = hasExplicitCost
                                        ? getFormattedPrice(getMethodCost(method), getMethodCurrency(method))
                                        : undefined;

                                    return (
                                        <ShippingMethodCard
                                            key={method.id}
                                            id={method.id}
                                            name={method.name}
                                            imageUrl={method.image?.default}
                                            price={costLabel}
                                            isSelected={selectedShippingMethod?.id === method.id}
                                            onClick={() => handleShippingMethodSelect(method.id)}
                                            isToAgree={isToAgreeMethod(method)}
                                            description={method.descripcion}
                                        />
                                    );
                                })}
                            </div>
                        </Col>

                        {selectedShippingMethod && selectedShippingMethod.descripcion && (
                            <Col xs={24} className="">
                                <div className="d-flex mt-2 p-2 fst-italy font-italic rounded" style={{ backgroundColor: 'var(--surface-elevated)' }}>
                                    <InfoCircleOutlined className="me-2 mr-2 text-warning" />
                                    <Typography.Text className="w-100 flex-grow small" style={{ color: 'var(--text-secondary)' }}>
                                        {selectedShippingMethod.descripcion}
                                    </Typography.Text>
                                </div>
                            </Col>
                        )}
                    </Row>
                ) : (
                    <Empty description="Actualmente no tenemos un método de envío a esa dirección." />
                )
            ) : (
                <p>Debes elegir o cargar una dirección de envío para poder calcular los costos.</p>
            )}
        </Card>
    );
};

interface TakeawaySectionProps {
    storeAddress: Address[];
    selectedBranch?: Address;
    onSelectBranch: (addr: Address) => void;
    className?: string;
    style?: React.CSSProperties;
}

export const TakeawaySection: React.FC<TakeawaySectionProps> = ({
    storeAddress,
    selectedBranch,
    onSelectBranch,
    className,
    style,
}) => (
    <Card
        data-testid="takeaway-section"
        title={(
            <Space align="center" size={8} wrap>
                <span>Dirección de Retiro</span>
                <Tag color="green">Predeterminado</Tag>
            </Space>
        )}
        className={className}
        style={style}
        variant="outlined"
    >
        <Text type="secondary" style={{ fontSize: '0.75rem' }} className="d-block mb-3">
            Priorizamos el retiro en tienda cuando está disponible. Podés cambiar a envío a domicilio en cualquier momento.
        </Text>
        <Radio.Group
            onChange={e => {
                const addr = storeAddress.find(a => a.id === e.target.value);
                if (addr) onSelectBranch(addr);
            }}
            value={selectedBranch?.id}
        >
            <Space orientation="vertical" className="w-100">
                {storeAddress.map(addr => (
                    <Radio key={addr.id} value={addr.id} className="w-100">
                        <ModernAddressDetails
                            address={addr}
                            className={`p-2 ${selectedBranch?.id === addr.id ? 'border-primary' : ''}`}
                        />
                    </Radio>
                ))}
            </Space>
        </Radio.Group>
    </Card>
);
